/** @jsxImportSource react */
import { useThemeLanguage } from '@/contexts/ThemeLanguageContext'
import './AdminPagination.css'

interface AdminPaginationProps {
  offset: number
  limit: number
  total?: number
  itemsLength: number
  loading?: boolean
  onNext: () => void
  onPrev: () => void
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: number[]
  /** Clave i18n para "Mostrando {from} - {to} de {total} …" */
  paginationSummaryKey?: string
}

export function AdminPagination({
  offset,
  limit,
  total,
  itemsLength,
  loading = false,
  onNext,
  onPrev,
  onPageSizeChange,
  pageSizeOptions,
  paginationSummaryKey = 'admin.users.paginationShowing',
}: AdminPaginationProps) {
  const { t } = useThemeLanguage()

  const rangeFrom = itemsLength === 0 ? 0 : offset + 1
  const rangeTo = offset + itemsLength

  const hasTotal = typeof total === 'number'
  const isNextDisabled = hasTotal ? offset + limit >= total : itemsLength < limit
  const showPageSize = Boolean(onPageSizeChange && pageSizeOptions && pageSizeOptions.length > 0)

  return (
    <div className="admin-pagination" data-testid="admin-pagination">
      <p className="admin-pagination-summary">
        {hasTotal
          ? t(paginationSummaryKey)
              .replace('{from}', String(rangeFrom))
              .replace('{to}', String(rangeTo))
              .replace('{total}', String(total))
          : t('admin.audit.offsetLabel').replace('{offset}', String(offset))}
      </p>

      {showPageSize && (
        <label className="admin-pagination-page-size" data-testid="admin-pagination-page-size">
          {t('admin.users.rowsPerPage')}
          <select
            value={limit}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label={t('admin.users.rowsPerPage')}
            disabled={loading}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="admin-pagination-nav">
        <button
          type="button"
          className="admin-pagination-btn-ghost"
          disabled={offset <= 0 || loading}
          onClick={onPrev}
        >
          {t('admin.users.paginationPrev')}
        </button>
        <button
          type="button"
          className="admin-pagination-btn-ghost"
          disabled={isNextDisabled || loading}
          onClick={onNext}
        >
          {t('admin.users.paginationNext')}
        </button>
      </div>
    </div>
  )
}
